import React, { FC, useState } from 'react';
import { Trans } from '@lingui/react';

import { AlertHelp, Pets, TrainingPilot } from '@rover/icons';
import { Box, Flex } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';
import CalloutBadge from '@rover/react-lib/src/components/callouts/CalloutBadge';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import TrainingUrls from '@rover/react-lib/src/pages/training/constants/urls';
import useInTrainingPilot from '@rover/react-lib/src/pages/training/hooks/useInTrainingPilot';
import { onSpaceOrEnterKeyPress } from '@rover/react-lib/src/utils/input';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';

import MenuItem from '../MenuItem';
import { dropdownClickHandler, fireEventForClickingOurServices } from '../utilities';

import MobileOurServices from './MobileOurServices';
import MobilePromoteYourProfileLink from './MobilePromoteYourProfileLink';
import MobileReferLink from './MobileReferLink';
import useMobileMenuItems from './useMobileMenuItems';

// this number needs to be larger than we expect the menu to render, but doesn't need to be exact
const MAX_HEIGHT = '600px';

export type Props = {
  showMenu: boolean;
};

const MobileMenu: FC<Props> = ({ showMenu }) => {
  const { colors } = useTheme();
  const [showOurServices, setShowOurServices] = useState<boolean>(false);
  const { isActiveServiceProvider, isAuthenticated, logout } = useCurrentUserShim();
  const mobileMenuItems = useMobileMenuItems();
  const { pathname } = useLocationContext();
  const { shouldShowTrainingNavbarLink } = useInTrainingPilot();
  const rolloutSitterPromotionEntryPoints = useFlag(FLAGS.ROLLOUT_SITTER_PROMOTION_M0_ENTRY_POINTS);

  const toggleOurServices = (): void => {
    setShowOurServices(!showOurServices);
    fireEventForClickingOurServices();
  };

  return (
    <Flex
      as="ul"
      onClick={dropdownClickHandler}
      pl="0x"
      width={['100%', '375px', '100%']}
      flexDirection="column"
      alignItems="stretch"
      background="navigation"
      borderBottom={`1px solid ${colors.border.primary}`}
      maxHeight={showMenu ? MAX_HEIGHT : '0'}
      display={['flex', 'flex', 'none']}
      // these styles only take effect when position is absolute
      top="100%"
      right="0"
      sx={{
        visibility: showMenu ? 'visible' : 'hidden',
        transition: `max-height 0.2s`,
        overflow: 'hidden',
        // tablet-breakpoint styles
        position: ['initial', 'absolute', 'initial'],
      }}
    >
      {mobileMenuItems.map((item) => (
        <MenuItem key={item.displayName} href={item.path} icon={item.icon}>
          <Trans id={item.displayName} />
        </MenuItem>
      ))}

      {!isAuthenticated && (
        <>
          <MenuItem
            onClick={toggleOurServices}
            onKeyPress={(e): void => onSpaceOrEnterKeyPress(e, toggleOurServices)}
            tabIndex={0}
            aria-expanded={showOurServices}
            icon={Pets}
          >
            <Trans id="Our Services" />
          </MenuItem>

          <MobileOurServices showOurServices={showOurServices} />

          {shouldShowTrainingNavbarLink && (
            <MenuItem href={TrainingUrls.SEARCH} icon={TrainingPilot}>
              Dog Training
              <Box ml="2x">
                <CalloutBadge value="NEW" />
              </Box>
            </MenuItem>
          )}

          <MenuItem
            href={`/account/continue/?bep=event%3Dnavigation-sign-up&next=${pathname}`}
            rel="nofollow"
          >
            <Trans id="Sign Up" />
          </MenuItem>

          <MenuItem
            href={`/account/continue/?bep=event%3Dnavigation-sign-in&action=sign_in&next=${pathname}`}
            rel="nofollow"
          >
            <Trans id="Sign In" />
          </MenuItem>
        </>
      )}

      <MenuItem href="/help/" icon={AlertHelp}>
        <Trans id="Help" />
      </MenuItem>

      {isAuthenticated && (
        <>
          <MenuItem
            forwardedAs="button"
            onClick={(): void =>
              logout &&
              logout({
                nextLocation: pathname,
                ref: 'header',
              })
            }
          >
            <Trans id="Log Out" />
          </MenuItem>

          {!isActiveServiceProvider && <MobileReferLink />}
          {rolloutSitterPromotionEntryPoints && isActiveServiceProvider && (
            <MobilePromoteYourProfileLink />
          )}
        </>
      )}
    </Flex>
  );
};

export default MobileMenu;
